import { checkCues, type Cue, type CueMap } from '@vrlearning/lesson-schema';
import type { StepControl, StepPhase } from './player.js';

/**
 * Voice as guide (ADR-035; docs/superpowers/specs/2026-09-29-voice-guide-design.md):
 * the pure rules, apart from the renderer and the audio so they can be tested.
 *
 * With the teacher's "Begeleiding: stem", Engelbert tells each explanation
 * step, one subtitle line shows the sentence he is saying, and where pupils
 * move freely the step moves on by itself after the voice and a short pause.
 * The text box comes back whenever the voice cannot carry a step.
 */

/** The class's guidance: the teacher's "Begeleiding: tekst / stem". */
export type Guide = 'text' | 'voice';

/**
 * How one step is guided: `text`, the text box as ever; `voice-text`, the
 * voice reads and paces the step over the text box (AR, speech without cues,
 * or a voice that is late); `voice-subtitles`, the panel's body hidden and the
 * spoken sentence on the subtitle plate.
 */
export type GuideRender = 'text' | 'voice-text' | 'voice-subtitles';

/** The voice starts this long after the step's panel would show: after a teleport's fade-in. */
export const GUIDE_LEAD_SECONDS = 0.5;
/**
 * The first voice after entering the world -- the guide's, or Voorlezen
 * reading by itself -- starts this long after the entry, not at once
 * (Pieter, 29 Sep 2026: "it should start after 2 seconds in the world"); a
 * step the class is moved to meanwhile starts no earlier either. Later steps
 * keep GUIDE_LEAD_SECONDS after their fade. See `worldEntry`.
 */
export const WORLD_ENTRY_VOICE_SECONDS = 2;
/** The step moves on this long after the voice (and the step's change) ends. */
export const GUIDE_PAUSE_SECONDS = 2.0;
/** A voice not started this long after it was due: the rest of the step shows the text box. */
export const GUIDE_STALL_SECONDS = 3;
/** After the teacher's pause, the voice resumes this long after the class does. */
export const GUIDE_RESUME_SECONDS = 0.5;
/** Switching to text, or an answer given while the voice reads, fades the voice out over this. */
export const GUIDE_CUT_SECONDS = 0.3;
/** A sentence shows this long before it is said... */
const CUE_LEAD = 0.1;
/** ...and the last one stays this long after it. */
const CUE_TAIL = 1.0;
/** A prediction's "Jij koos: …" waits this long before the reveal. */
export const PREDICTED_SECONDS = 4;

/** A session state's `guide`: voice only for exactly `'voice'`, so an older API reads as text. */
export function guideOf(value: unknown): Guide {
  return value === 'voice' ? 'voice' : 'text';
}

/** What the address asks for, `?guide=voice` or `?guide=text`; undefined when it asks nothing. */
export function askedGuide(href: string): Guide | undefined {
  let asked: string | null;
  try {
    asked = new URL(href).searchParams.get('guide');
  } catch {
    return undefined;
  }
  return asked === 'voice' || asked === 'text' ? asked : undefined;
}

/** `?guide=voice`: guide mode outside a class (practising alone, `?play=`, the desktop preview). */
export function guideFromUrl(href: string): Guide {
  return askedGuide(href) ?? 'text';
}

export function guideFor(input: {
  guide: Guide;
  /** The class's Voorlezen; undefined until the first `session.state` has said it. */
  voiceSwitch: boolean | undefined;
  mode: 'vr' | 'ar';
  /** The step has a voice-over this headset can play. */
  hasVoice: boolean;
  /** ...and cues for it. */
  hasCues: boolean;
  /** Its voice is more than GUIDE_STALL_SECONDS late. */
  stalled: boolean;
  /**
   * The step is a task whose text must stay while the voice reads over it: furnishing (ADR-037),
   * where the pupil works with the lectern's rooms and palette under the text.
   */
  keepText?: boolean;
}): GuideRender {
  if (input.guide !== 'voice' || input.voiceSwitch !== true || !input.hasVoice) return 'text';
  if (input.mode === 'ar' || !input.hasCues || input.stalled || input.keepText === true)
    return 'voice-text';
  return 'voice-subtitles';
}

/**
 * The cue to show at `t` seconds into the voice: cue i from 0.1 s before it
 * starts until 0.1 s before the next one starts -- held through the silence,
 * so there is no blank between sentences -- and the last one until 1 s past
 * its end. Undefined before the first and after the last.
 */
export function cueAt(cues: readonly Pick<Cue, 'start' | 'end'>[], t: number): number | undefined {
  if (cues.length === 0 || t < cues[0]!.start - CUE_LEAD) return undefined;
  for (let i = cues.length - 1; i >= 0; i--) {
    if (t >= cues[i]!.start - CUE_LEAD) {
      if (i === cues.length - 1 && t > cues[i]!.end + CUE_TAIL) return undefined;
      return i;
    }
  }
  return undefined;
}

/**
 * Where a voice cut at `t` resumes (decision 9): the start of the sentence it
 * was saying, or of the next when it was cut in the silence between two.
 * Undefined once the last sentence has been said. Without cues, where it was cut.
 */
export function cueStartAt(
  cues: readonly Pick<Cue, 'start' | 'end'>[],
  t: number,
): number | undefined {
  if (cues.length === 0) return t;
  for (const cue of cues) {
    if (t < cue.end) return cue.start;
  }
  return undefined;
}

/** A piece of a cue as the plate shows it: the whole sentence, or one page of it. */
export type Page = { start: number; end: number; text: string };

/**
 * A cue that does not fit on two lines at the smallest size, broken into
 * pages: at the last ", " that fits when it leaves the page at least half
 * full (a page of "Ja," alone reads as a flicker), else at the last space
 * that fits. The cue's time is shared out by characters.
 */
export function pages(cue: Page, fits: (text: string) => boolean): Page[] {
  if (fits(cue.text)) return [{ ...cue }];
  const words = cue.text.split(' ');
  const texts: string[] = [];
  let at = 0;
  while (at < words.length) {
    let end = at + 1;
    while (end < words.length && fits(words.slice(at, end + 1).join(' '))) end++;
    if (end < words.length) {
      const fitting = words.slice(at, end).join(' ').length;
      for (let comma = end; comma > at + 1; comma--) {
        if (!words[comma - 1]!.endsWith(',')) continue;
        if (words.slice(at, comma).join(' ').length * 2 >= fitting) end = comma;
        break;
      }
    }
    texts.push(words.slice(at, end).join(' '));
    at = end;
  }
  const letters = texts.reduce((sum, text) => sum + text.length, 0);
  const seconds = cue.end - cue.start;
  const paged: Page[] = [];
  let start = cue.start;
  for (const [i, text] of texts.entries()) {
    const end = i === texts.length - 1 ? cue.end : start + (seconds * text.length) / letters;
    paged.push({ start, end, text });
    start = end;
  }
  return paged;
}

const words = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');

/**
 * When a prediction's question and answers appear (decision 4): as the first
 * cue whose words open the question begins -- or, a cue carrying more than
 * the question, whose words begin with it. When none does (the narration
 * words the question differently), the last cue that ends in "?". Undefined
 * when no cue does either: then at the voice's end.
 */
export function questionCue(cues: readonly Cue[], question: string): number | undefined {
  const asked = words(question);
  if (asked === '') return undefined;
  for (const cue of cues) {
    const said = words(cue.text);
    if (said === '') continue;
    if (asked === said || asked.startsWith(`${said} `) || said.startsWith(`${asked} `)) {
      return cue.start;
    }
  }
  for (let i = cues.length - 1; i >= 0; i--) {
    if (cues[i]!.text.trim().endsWith('?')) return cues[i]!.start;
  }
  return undefined;
}

/** How long an answer's outcome is read before the step moves on: clamp(2 + characters/10, 4, 15) s. */
export function outcomeSeconds(text: string): number {
  return Math.min(15, Math.max(4, 2 + text.length / 10));
}

export type AdvanceInput = {
  control: StepControl;
  phase: StepPhase;
  isLast: boolean;
  hotspotOpen: boolean;
  paused: boolean;
  /** When the voice ended (or an answer cut it), on the pacer's clock; undefined while it is still to finish. */
  voiceEnded: number | undefined;
  /** When the step's change ends; 0 when it has none. */
  changeEnded: number;
  /** After an answer: when its outcome has been read (`outcomeSeconds`). */
  readEnded?: number | undefined;
  /** When the last hold ended (a hotspot card closed, the class resumed): the full pause runs again from there. */
  heldUntil?: number | undefined;
};

/**
 * The phase the pacer is told for a step: a furnish step (ADR-037) is a panel to the player -- its
 * Verder shows in a free class -- but a task to the pacer, which never moves it on by itself: a
 * furnish step has no outcome to wait for.
 */
export function pacedPhase(phase: StepPhase, task: { type: string } | undefined): StepPhase {
  return task?.type === 'furnish' && phase === 'panel' ? 'task' : phase;
}

/**
 * When to call `player.next()`, on the pacer's clock, if at all: only in a
 * free class (practising alone, `?play=`, the demo), never from the last step
 * and never while a hotspot card is open or the class is paused; in phase
 * `panel` the pause after the voice and the step's change, in phase `result`
 * once the outcome has been read. A question, a task and a prediction's
 * introduction move on only when answered.
 */
export function advanceAfter(input: AdvanceInput): number | undefined {
  if (input.control !== 'free' || input.isLast || input.hotspotOpen || input.paused) {
    return undefined;
  }
  if (input.voiceEnded === undefined) return undefined;
  const held = input.heldUntil ?? 0;
  const settled = Math.max(input.voiceEnded, input.changeEnded, held) + GUIDE_PAUSE_SECONDS;
  if (input.phase === 'panel') return settled;
  if (input.phase === 'result') {
    return input.readEnded === undefined ? undefined : Math.max(input.readEnded, settled);
  }
  return undefined;
}

/** A cue map as it arrived (`session.assigned`, a lesson route, the demo): only what checkCues accepts. */
export function cuesFrom(raw: unknown): CueMap {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const kept: Record<string, readonly Cue[]> = {};
  for (const [id, cues] of Object.entries(raw)) {
    if (checkCues(cues) === undefined) kept[id] = cues as Cue[];
  }
  return kept;
}

/**
 * When the pupil is in the lesson's world (Pieter, 29 Sep 2026): what may
 * start. In a headset, the world is entered when the XR session has started
 * ("Start in VR" lets the sound play, but the ambience fades in only once the
 * session is there) and the scene has loaded; a lesson pushed while already
 * in VR is entered when its scene has loaded. In a browser (the desktop
 * preview, a played lesson, the demo on a laptop) the ambience starts when
 * the scene has loaded, and the first voice counts from the later of that and
 * the sound being let play (a click or key; sometimes at once). `headset`:
 * whether this browser can enter the lesson's XR mode; undefined, not known
 * yet.
 */
export function worldEntry(input: {
  sceneLoaded: boolean;
  headset: boolean | undefined;
  presenting: boolean;
  unlocked: boolean;
}): { ambient: boolean; voice: boolean } {
  const ambient = input.sceneLoaded && (input.presenting || input.headset === false);
  return { ambient, voice: ambient && (input.presenting || input.unlocked) };
}
